// Single source of truth for the IEP behavior-of-concern "Settings in which the
// behavior occurs" field. Used by BOTH:
//   • the therapist Behavior Tracking data sheet (the setting/context dropdown)
//   • the supervisor / child Behavior Analysis report
// so that editing an IEP behavior's settings is reflected identically in both
// places — the parsing and the resulting option list never diverge.
//
// The settings live as a free-text comma-separated string on each behavior, e.g.
// "School classroom, therapy sessions, home routines, and group learning".

/**
 * Parse the free-text settings string into a clean list.
 * Splits on comma, strips leading "and", trailing punctuation and NBSP/BOM, drops empties.
 * @param {string} raw
 * @returns {string[]}
 */
export const parseSettingsString = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map((s) => s.replace(/^[\s﻿\xA0]+|[\s﻿\xA0]+$/g, '')) // trim incl. NBSP / BOM
    .map((s) => s.replace(/^and\s+/i, '').trim())
    .map((s) => s.replace(/[.!?;]+$/, '').trim())
    .filter((s) => s.length > 0);
};

/**
 * Deduped union (case-insensitive, original casing preserved) of every setting
 * declared across a list of behaviors. Each behavior carries its settings in a
 * free-text `settings` field.
 * @param {Array<{settings?: string}>} behaviors
 * @returns {string[]}
 */
export const getUniqueBehaviorSettings = (behaviors = []) => {
  const map = new Map();
  behaviors.forEach((b) => {
    parseSettingsString(b?.settings).forEach((s) => {
      const key = s.toLowerCase();
      if (!map.has(key)) map.set(key, s);
    });
  });
  return [...map.values()];
};

// Shared bubble-radius scale for "Behavior Analysis" scatter/bubble charts. Used by BOTH:
//   • the supervisor Reports "Behavior Analysis" scatter chart (Highcharts)
//   • the child-specific "Behaviors of Concern" bubble chart (ApexCharts)
// so the same incident count always renders as the same bubble size in both places.
const BEHAVIOR_BUBBLE_MIN_RADIUS = 4;
const BEHAVIOR_BUBBLE_MAX_RADIUS = 12;

/**
 * Pixel radius for a behavior bubble given its incident count.
 * @param {number} count
 * @returns {number}
 */
export const getBehaviorBubbleRadius = (count) => Math.min(BEHAVIOR_BUBBLE_MIN_RADIUS + (count ?? 0), BEHAVIOR_BUBBLE_MAX_RADIUS);
